import { useMemo } from "react";
import { Icon } from "@/components/Icon/Icon";
import type {
  CompareCatalog,
  CompareFormula,
  ComputeCompareStats,
} from "@/utils/types/compare";
import type { FormulationEntry } from "@/utils/types/compound";

const MAX_PICKS = 2;

type CompareSelectPopoverProps = {
  catalog: CompareCatalog;
  selected: CompareFormula[];
  onToggle: (f: CompareFormula) => void;
  onClear: () => void;
  // Per-row metric helper so the user can compare Loss / Cost while picking.
  computeStats: ComputeCompareStats;
  /** Current Flask formulation — used as the Δ baseline so each row can
   *  show how it compares (loss-down green, loss-up amber). */
  currentFormulation: FormulationEntry[];
  /** When true, the similar/AI sections render "Loading…" copy instead of
   *  their "No …" empty-state copy. */
  suggestionsLoading?: boolean;
};

// Δ formatter — null when within ±0.005 so rows for ~zero deltas show "·"
// instead of "+0.00" / "−0.00".
function fmtDelta(d: number | null, decimals = 2): string | null {
  if (d == null || Math.abs(d) < 0.005) return null;
  const sign = d > 0 ? "+" : "−";
  return sign + Math.abs(d).toFixed(decimals);
}

type RowProps = {
  formula: CompareFormula;
  picked: boolean;
  disabled: boolean;
  onClick: () => void;
  computeStats: ComputeCompareStats;
  // Δ baseline metrics from the Current Flask, computed once per popover.
  curLossL2Sq: number | null;
  curCost: number | null;
};

function Row({
  formula,
  picked,
  disabled,
  onClick,
  computeStats,
  curLossL2Sq,
  curCost,
}: RowProps) {
  // Stat resolution per source:
  //   - AI: use server-provided final_loss_l2_sq directly; cost out of scope.
  //   - Yours: client-compute loss & cost from the formulation, then SQUARE
  //     the loss so the L² column is apples-to-apples with AI's squared value.
  //   - Similar: empty formulation → both stats null (renders as "—").
  const stats = computeStats(formula.formulation);
  let lossVal: number | null = null;
  let costVal: number | null = null;
  if (formula.source === "ai") {
    lossVal =
      formula.finalLossL2Sq != null && Number.isFinite(formula.finalLossL2Sq)
        ? formula.finalLossL2Sq
        : null;
    costVal = null;
  } else if (formula.source === "yours") {
    const baseLoss =
      stats.loss != null && !Number.isNaN(stats.loss) ? stats.loss : null;
    lossVal = baseLoss != null ? baseLoss * baseLoss : null;
    costVal =
      stats.cost && stats.cost.usdPer100g != null
        ? stats.cost.usdPer100g
        : null;
  } else {
    // similar — empty formulation today
    lossVal = null;
    costVal = null;
  }

  const lossDelta =
    lossVal != null && curLossL2Sq != null ? lossVal - curLossL2Sq : null;
  const costDelta =
    costVal != null && curCost != null ? costVal - curCost : null;
  const lossDeltaStr = fmtDelta(lossDelta, 2);
  const costDeltaStr = fmtDelta(costDelta, 2);
  const lossStr = lossVal != null ? lossVal.toFixed(2) : null;
  const costStr = costVal != null ? `$${costVal.toFixed(2)}` : null;

  // Subtitle is just "{N} items" — substance preview was noisy and the
  // metrics column on the right is what flavorists actually compare on.
  const itemCount =
    typeof formula.descriptor === "string" ? 0 : formula.descriptor.count;

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="cmp-pickrow"
      data-picked={picked || undefined}
      data-disabled={disabled || undefined}
    >
      <span className="cmp-checkbox">
        {picked && <Icon name="check" size={9} />}
      </span>
      <div
        className="vstack"
        style={{ minWidth: 0, gap: 2, lineHeight: 1.25 }}
      >
        <span
          style={{
            fontSize: 12.5,
            fontWeight: 500,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {formula.name}
        </span>
        <span className="mono" style={{ fontSize: 10, color: "var(--ink-4)" }}>
          {itemCount} items
        </span>
      </div>
      {/* Metrics — Loss + Cost / 100g, stacked on the right with a Δ
          column so the user can scan how each formula compares to their
          Current Flask. Loss-down good (green); cost-down good (green). */}
      <div
        className="vstack mono"
        style={{
          alignItems: "stretch",
          gap: 2,
          lineHeight: 1.15,
          flexShrink: 0,
          fontVariantNumeric: "tabular-nums",
          minWidth: 120,
        }}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "10px 1fr auto",
            gap: 5,
            alignItems: "baseline",
          }}
        >
          <span style={{ fontSize: 9, color: "var(--ink-4)" }}>L²</span>
          <span
            style={{
              fontSize: 11.5,
              color: "var(--ink)",
              fontWeight: 700,
              textAlign: "right",
            }}
          >
            {lossStr || "—"}
          </span>
          <span
            style={{
              fontSize: 10,
              fontWeight: 400,
              minWidth: 46,
              textAlign: "right",
              color: lossDeltaStr
                ? lossDelta! < 0
                  ? "var(--brand-green)"
                  : "var(--brand-amber)"
                : "var(--ink-mute)",
            }}
          >
            {lossDeltaStr ? `(${lossDeltaStr})` : "·"}
          </span>
        </div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "10px 1fr auto",
            gap: 5,
            alignItems: "baseline",
          }}
        >
          <span style={{ fontSize: 9, color: "var(--ink-4)" }}>$</span>
          <span
            style={{
              fontSize: 11,
              color: "var(--ink)",
              fontWeight: 700,
              textAlign: "right",
            }}
          >
            {costStr || "—"}
          </span>
          <span
            style={{
              fontSize: 10,
              fontWeight: 400,
              minWidth: 46,
              textAlign: "right",
              color: costDeltaStr
                ? costDelta! < 0
                  ? "var(--brand-green)"
                  : "var(--brand-amber)"
                : "var(--ink-mute)",
            }}
          >
            {costDeltaStr ? `(${costDeltaStr})` : "·"}
          </span>
        </div>
      </div>
    </button>
  );
}

type SectionProps = {
  icon: "folder" | "wand";
  label: string;
  items: CompareFormula[];
  emptyText: string;
  isPicked: (id: string) => boolean;
  atMax: boolean;
  onToggle: (f: CompareFormula) => void;
  computeStats: ComputeCompareStats;
  curLossL2Sq: number | null;
  curCost: number | null;
};

function Section({
  icon,
  label,
  items,
  emptyText,
  isPicked,
  atMax,
  onToggle,
  computeStats,
  curLossL2Sq,
  curCost,
}: SectionProps) {
  return (
    <div>
      <div className="cmp-popover-section-head">
        <div className="hstack gap-2">
          <Icon name={icon} size={11} />
          <span
            className="mono"
            style={{
              fontSize: 9,
              letterSpacing: "0.1em",
              textTransform: "uppercase",
              color: "var(--ink-4)",
              fontWeight: 500,
            }}
          >
            {label}
          </span>
        </div>
        <span
          className="mono"
          style={{ fontSize: 9, color: "var(--ink-4)" }}
        >
          {items.length}
        </span>
      </div>
      {items.length === 0 ? (
        <div className="cmp-popover-section-empty">{emptyText}</div>
      ) : (
        <div className="vstack" style={{ gap: 1, padding: "0 4px 4px" }}>
          {items.map((f) => {
            const picked = isPicked(f.id);
            const disabled = !picked && atMax;
            return (
              <Row
                key={f.id}
                formula={f}
                picked={picked}
                disabled={disabled}
                onClick={() => !disabled && onToggle(f)}
                computeStats={computeStats}
                curLossL2Sq={curLossL2Sq}
                curCost={curCost}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}

export function CompareSelectPopover({
  catalog,
  selected,
  onToggle,
  onClear,
  computeStats,
  currentFormulation,
  suggestionsLoading = false,
}: CompareSelectPopoverProps) {
  const isPicked = (id: string) => selected.some((s) => s.id === id);
  const atMax = selected.length >= MAX_PICKS;

  // Δ baseline — squared so the AI rows (which carry L²-squared from the
  // server) and the yours rows (squared client-side in Row) compare on the
  // same scale.
  const { curLossL2Sq, curCost } = useMemo(() => {
    const curStats = computeStats(currentFormulation);
    const curLoss =
      curStats.loss != null && !Number.isNaN(curStats.loss)
        ? curStats.loss
        : null;
    return {
      curLossL2Sq: curLoss != null ? curLoss * curLoss : null,
      curCost:
        curStats.cost && curStats.cost.usdPer100g != null
          ? curStats.cost.usdPer100g
          : null,
    };
  }, [computeStats, currentFormulation]);

  return (
    <div className="wb-pop" style={{ width: 380 }}>
      <div
        className="wb-pop-header"
        style={{ display: "block", padding: "10px 14px 11px" }}
      >
        <div
          className="hstack"
          style={{ justifyContent: "space-between", alignItems: "baseline" }}
        >
          <span className="label">Compare formulae</span>
          <span
            className="mono"
            style={{
              fontSize: 10,
              color:
                selected.length > 0 ? "var(--brand-green)" : "var(--ink-4)",
              fontWeight: selected.length > 0 ? 600 : 400,
            }}
          >
            {selected.length} / {MAX_PICKS} picked
          </span>
        </div>
        <span
          style={{
            fontSize: 11.5,
            color: "var(--ink-3)",
            lineHeight: 1.45,
            display: "block",
            marginTop: 4,
          }}
        >
          Pick up to {MAX_PICKS} other formulae of this flavor — diff
          side-by-side with your Current Flask.
        </span>
      </div>

      <div className="cmp-popover-body">
        <Section
          icon="folder"
          label="Current Flavor Formulae"
          items={catalog.yours}
          emptyText="No other variants of this flavor yet — fork the Current Flask to create one."
          isPicked={isPicked}
          atMax={atMax}
          onToggle={onToggle}
          computeStats={computeStats}
          curLossL2Sq={curLossL2Sq}
          curCost={curCost}
        />
        <div className="cmp-popover-divider" />
        <Section
          icon="folder"
          label="Other Similar Formulae"
          items={catalog.similar}
          emptyText={
            suggestionsLoading
              ? "Loading…"
              : "No other flavors in your org share this target yet."
          }
          isPicked={isPicked}
          atMax={atMax}
          onToggle={onToggle}
          computeStats={computeStats}
          curLossL2Sq={curLossL2Sq}
          curCost={curCost}
        />
        <div className="cmp-popover-divider" />
        <Section
          icon="wand"
          label="AI Generated Formulae"
          items={catalog.ai}
          emptyText={
            suggestionsLoading
              ? "Loading…"
              : "No AI-generated alternatives available for this target."
          }
          isPicked={isPicked}
          atMax={atMax}
          onToggle={onToggle}
          computeStats={computeStats}
          curLossL2Sq={curLossL2Sq}
          curCost={curCost}
        />
      </div>

      {selected.length > 0 && (
        <div className="wb-pop-footer">
          <span
            className="mono"
            style={{ fontSize: 10, color: "var(--ink-4)" }}
          >
            Comparison shown in flask region
          </span>
          <button
            type="button"
            className="btn xs ghost"
            style={{ padding: "3px 8px" }}
            onClick={onClear}
          >
            Clear
          </button>
        </div>
      )}
    </div>
  );
}
